/**
 * Comprehensive Password Validation Utility
 * Follows modern security best practices including OWASP guidelines
 */

// Common weak passwords (top 100 most used passwords)
const COMMON_PASSWORDS = [
  'password', 'password123', '123456', '123456789', 'qwerty', 'abc123',
  'password1', 'admin', 'letmein', 'welcome', 'monkey', 'dragon', 'master',
  'shadow', 'superman', 'michael', 'football', 'batman', 'trustno1',
  'hello', 'charlie', 'aa123456', 'donald', 'password12', 'login',
  'princess', 'solo', 'qwerty123', 'starwars', 'cheese', 'mustang',
  'camaro', 'baseball', 'jordan', 'harley', 'ranger', 'iwantu', 'jennifer',
  'hunter', 'fuckyou', 'amanda', 'passw0rd', 'loveme', 'whatever',
  'andrew', 'joshua', 'summer', 'daniel', 'jessica', 'martin'
];

// Keyboard patterns to avoid
const KEYBOARD_PATTERNS = [
  'qwerty', 'qwertyui', 'asdf', 'asdfgh', 'zxcv', 'zxcvbn',
  '1234', '12345', '123456', '1234567', '12345678', '123456789',
  'abcd', 'abcde', 'abcdef', 'abcdefg'
];

/**
 * Validates password against security requirements
 * @param {string} password - The password to validate
 * @returns {boolean} - True if password meets all requirements
 */
export const validatePassword = (password) => {
  if (!password) return false;
  
  const checks = {
    length: password.length >= 8,
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    number: /\d/.test(password),
    special: /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(password),
    noSpaces: !/\s/.test(password)
  };
  
  return Object.values(checks).every(check => check);
};

/**
 * Gets detailed password requirements with their status
 * @param {string} password - The password to check
 * @returns {Array} - Array of requirement objects
 */
export const getPasswordRequirements = (password = '') => {
  return [
    {
      key: 'length',
      test: password.length >= 8,
      message: 'At least 8 characters',
      met: password.length >= 8
    },
    {
      key: 'lowercase',
      test: /[a-z]/.test(password),
      message: 'One lowercase letter (a-z)',
      met: /[a-z]/.test(password)
    },
    {
      key: 'uppercase',
      test: /[A-Z]/.test(password),
      message: 'One uppercase letter (A-Z)',
      met: /[A-Z]/.test(password)
    },
    {
      key: 'number',
      test: /\d/.test(password),
      message: 'One number (0-9)',
      met: /\d/.test(password)
    },
    {
      key: 'special',
      test: /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(password),
      message: 'One special character (!@#$%^&*)',
      met: /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(password)
    },
    {
      key: 'noSpaces',
      test: !/\s/.test(password),
      message: 'No spaces allowed',
      met: !/\s/.test(password)
    }
  ];
};

/**
 * Calculates password strength score (0-8)
 * @param {string} password - The password to evaluate
 * @param {string} email - User's email for comparison (optional)
 * @returns {number} - Strength score from 0 to 8
 */
export const getPasswordStrength = (password, email = '') => {
  if (!password) return 0;
  
  let strength = 0;
  const requirements = getPasswordRequirements(password);
  
  // Base strength from requirements (0-6 points)
  const metRequirements = requirements.filter(req => req.met).length;
  strength = metRequirements;
  
  // Bonus points for longer passwords
  if (password.length >= 12) strength += 1;
  if (password.length >= 16) strength += 1;
  
  // Penalty for common patterns
  const commonPatterns = [
    /123456/,
    /password/i,
    /qwerty/i,
    /admin/i,
    /letmein/i,
    /welcome/i,
    /(.)\1{2,}/, // repeated characters (aaa, 111)
  ];
  
  if (commonPatterns.some(pattern => pattern.test(password))) {
    strength = Math.max(0, strength - 2);
  }
  
  // Check for email similarity
  if (email && password.toLowerCase().includes(email.split('@')[0].toLowerCase())) {
    strength = Math.max(0, strength - 1);
  }
  
  return Math.min(strength, 8); // Cap at 8 for display purposes
};

/**
 * Gets password strength label and color
 * @param {number} strength - Strength score (0-8)
 * @returns {Object} - Object with label and color
 */
export const getPasswordStrengthLabel = (strength) => {
  if (strength === 0) return { label: '', color: '' };
  if (strength <= 2) return { label: 'Very Weak', color: 'var(--color-error-text)' };
  if (strength <= 3) return { label: 'Weak', color: 'var(--color-strength-weak)' };
  if (strength <= 4) return { label: 'Fair', color: 'var(--color-strength-fair)' };
  if (strength <= 5) return { label: 'Good', color: 'var(--color-success-text)' };
  if (strength <= 6) return { label: 'Strong', color: 'var(--color-strength-strong)' };
  return { label: 'Very Strong', color: 'var(--color-strength-very-strong)' };
};

/**
 * Checks for security issues in password
 * @param {string} password - The password to check
 * @param {string} email - User's email for comparison (optional)
 * @returns {Array} - Array of security issue messages
 */
export const checkPasswordSecurity = (password, email = '') => {
  const issues = [];
  
  if (!password) return issues;
  
  // Check for common weak passwords
  if (COMMON_PASSWORDS.includes(password.toLowerCase())) {
    issues.push('This is a commonly used password');
  }
  
  // Check for keyboard patterns
  if (KEYBOARD_PATTERNS.some(pattern => password.toLowerCase().includes(pattern))) {
    issues.push('Avoid keyboard patterns');
  }
  
  // Check for repeated characters
  if (/(.)\1{2,}/.test(password)) {
    issues.push('Avoid repeating characters');
  }
  
  // Check for personal information
  if (email) {
    const emailUsername = email.split('@')[0].toLowerCase();
    if (password.toLowerCase().includes(emailUsername)) {
      issues.push('Don\'t use your email in your password');
    }
  }
  
  // Check for simple sequences
  if (/012|123|234|345|456|567|678|789|890|abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz/i.test(password)) {
    issues.push('Avoid sequential characters');
  }
  
  // Check for year patterns
  const currentYear = new Date().getFullYear();
  for (let year = currentYear - 50; year <= currentYear + 10; year++) {
    if (password.includes(year.toString())) {
      issues.push('Avoid using years in your password');
      break;
    }
  }
  
  return issues;
};

/**
 * Generates password suggestions
 * @returns {Array} - Array of password suggestion tips
 */
export const getPasswordSuggestions = () => {
  return [
    'Use a passphrase: "Coffee-Morning-Sunshine-42!"',
    'Combine unrelated words with numbers and symbols',
    'Use the first letter of each word in a sentence',
    'Replace letters with numbers and symbols (a→@, e→3, i→!)',
    'Use a password manager to generate and store strong passwords',
    'Consider using 12+ characters for extra security'
  ];
};

/**
 * Comprehensive password validation with detailed feedback
 * @param {string} password - The password to validate
 * @param {string} email - User's email for comparison (optional)
 * @returns {Object} - Complete validation result
 */
export const validatePasswordComprehensive = (password, email = '') => {
  const isValid = validatePassword(password);
  const requirements = getPasswordRequirements(password);
  const strength = getPasswordStrength(password, email);
  const strengthInfo = getPasswordStrengthLabel(strength);
  const securityIssues = checkPasswordSecurity(password, email);
  
  let message = '';
  if (!password) {
    message = '';
  } else if (!isValid) {
    const unmetRequirements = requirements.filter(req => !req.met);
    if (unmetRequirements.length > 0) {
      message = `Missing: ${unmetRequirements.map(req => req.message.toLowerCase()).join(', ')}`;
    }
  } else {
    if (securityIssues.length > 0) {
      message = `${strengthInfo.label} - ${securityIssues[0]}`;
    } else {
      message = `${strengthInfo.label} password`;
    }
  }
  
  return {
    isValid,
    message,
    strength,
    strengthInfo,
    requirements,
    securityIssues,
    suggestions: getPasswordSuggestions()
  };
};